import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEventStore } from '../store/eventStore';
import { useGroupStore } from '../store/groupStore';
import { eventService } from '../services/eventService';
import { groupService } from '../services/groupService';
import { premiumService } from '../services/premiumService';
import { Button, Input, NoticeContainer, FeaturedImageUpload, EventSearchSelect } from '../components/ui';
import { toast } from '../components/ui/Toaster';
import { UpgradeRequiredError } from '../types/premiumTypes';
import UpgradeModal from '../components/modals/UpgradeModal';
import { useNotice } from '../contexts/NoticeContext';
import type { CreateGroupRequest } from '../types';

const CreateGroupPage: React.FC = () => {
  
  const navigate = useNavigate();
  const location = useLocation();
  const { activeEvents, setActiveEvents } = useEventStore();
  const { addGroup } = useGroupStore();
  const { addNotice } = useNotice();
  
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<CreateGroupRequest>({
    name: '',
    description: '',
    eventId: 0,
  });
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [canCreateGroup, setCanCreateGroup] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    loadEvents();
    checkGroupLimit();
  }, []);

  // Handle URL parameter for event pre-selection
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const eventId = urlParams.get('eventId');
    
    if (eventId && parseInt(eventId) > 0) {
      setFormData(prev => ({ ...prev, eventId: parseInt(eventId) }));
    }
  }, [location.search]);

  const checkGroupLimit = async () => {
    try {
      const result = await premiumService.canCreateGroup();
      setCanCreateGroup(result.canJoin);
      
      if (!result.canJoin && result.requiresUpgrade) {
        // Show notice when user first lands on page and is at limit
        addNotice({
          type: 'warning',
          title: 'Groep limiet bereikt',
          message: result.reason || 'Je kunt op dit moment maar in 3 groepen tegelijkertijd. Wil je meer? Steun ons dan met een biertje (€4,95) en dan kun je voor altijd in 6 groepen tegelijkertijd.',
          persistenceKey: 'group_limit_reached',
          dismissible: true,
          autoExpire: 15 * 1000, // 15 seconds
        });
      } else {
      }
    } catch (error) {
      console.error('❌ Failed to check group limit:', error);
    }
  };

  const loadEvents = async (search?: string) => {
    try {
      const response = await eventService.getActiveEvents(
        search ? { limit: 50, search } : { limit: 50 }
      );
      setActiveEvents(response);
    } catch (error) {
      console.error('Failed to load events:', error);
      toast.error('Fout', 'Laden van evenementen mislukt');
    }
  };

  const handleEventSearchChange = (term: string) => {
    loadEvents(term || undefined);
  };

  const handleInputChange = (field: keyof CreateGroupRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileSelect = (file: File | null) => {
    setFeaturedImage(file);
    
    // Clear error when file is selected
    if (errors.featuredImage) {
      setErrors(prev => ({ ...prev, featuredImage: '' }));
    }
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Groepsnaam is verplicht';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Groepsnaam moet minimaal 3 karakters zijn';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Beschrijving is verplicht';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Beschrijving moet minimaal 10 karakters zijn';
    }

    if (!formData.eventId) {
      newErrors.eventId = 'Selecteer een evenement';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      
      let groupData;
      
      if (featuredImage) {
        // Use FormData when there's a file
        const formDataObj = new FormData();
        formDataObj.append('name', formData.name);
        formDataObj.append('description', formData.description);
        formDataObj.append('eventId', formData.eventId.toString());
        formDataObj.append('featuredImage', featuredImage);
        groupData = formDataObj;
      } else {
        // Use regular object when no file
        groupData = {
          name: formData.name,
          description: formData.description,
          eventId: formData.eventId,
        };
      }
      
      const group = await groupService.createGroup(groupData);
      
      addGroup(group);
      toast.success('Succes!', 'Groep succesvol aangemaakt');
      navigate(`/groups/${group.id}`);
    } catch (error: any) {
      console.error('Failed to create group:', error);
      
      // Handle upgrade required error
      if (error instanceof UpgradeRequiredError) {
        addNotice({
          type: 'warning',
          title: 'Groep limiet bereikt',
          message: error.message || 'Je kunt op dit moment maar in 3 groepen tegelijkertijd. Wil je meer? Steun ons dan met een biertje (€4,95) en dan kun je voor altijd in 6 groepen tegelijkertijd.',
          persistenceKey: 'group_creation_limit_reached',
          dismissible: true,
          autoExpire: 15 * 1000, // 15 seconds
        });
        setShowUpgradeModal(true);
      } else {
        toast.error('Fout', error.message || 'Aanmaken groep mislukt');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[760px]">
      {/* Header */}
      <button
        onClick={() => navigate('/groups')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-fg-muted transition-colors hover:text-fg-bright"
      >
        <ArrowLeft className="h-4 w-4" />
        Terug naar groepen
      </button>
      <div className="sf-eyebrow mb-3">// nieuwe groep</div>
      <h1 className="sf-h1">Nieuwe groep aanmaken</h1>
      <p className="mb-7 mt-2.5 text-base text-fg-muted">
        Start een groep voor je festival — en verzamel je crew.
      </p>

      {/* Form */}
      <div className="sf-card rounded-[22px] p-5 sm:p-7">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Group Name */}
          <Input
            label="Groepsnaam"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={errors.name}
            placeholder="Bijv. electronic music liefhebbers"
            maxLength={100}
            required
          />

          {/* Description */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-fg-soft">
              Beschrijving
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Vertel wat voor mensen je zoekt voor je groep, wat je minimaal wil gaan doen daar, alles wat relevant is ..."
              className={`sf-input min-h-[110px] resize-y ${
                errors.description ? '!border-neon-pink/60' : ''
              }`}
              required
            />
            <div className="mt-1.5 flex items-center justify-between">
              {errors.description && (
                <p className="text-sm text-neon-pink-soft">{errors.description}</p>
              )}
              <p className="ml-auto font-mono text-xs text-fg-meta">
                {formData.description.length}/500
              </p>
            </div>
          </div>

          {/* Event Selection */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-fg-soft">
              Festival / Evenement
            </label>
            <EventSearchSelect
              value={formData.eventId}
              onChange={(eventId) => handleInputChange('eventId', eventId)}
              events={activeEvents}
              error={errors.eventId}
              placeholder="Kies een evenement"
              onSearchChange={handleEventSearchChange}
              required
            />

            {/* Event Creation Link */}
            <div className="sf-notice-info mt-3">
              Kun je het gewenste evenement niet vinden?{' '}
              <Link to="/events/create" className="sf-link">
                Maak je eigen evenement aan!
              </Link>
            </div>
          </div>

          {/* Featured Image */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-fg-soft">
              Afbeelding <span className="font-normal text-fg-meta">(optioneel)</span>
            </label>
            <FeaturedImageUpload
              onFileSelect={handleFileSelect}
              {...(errors.featuredImage && { error: errors.featuredImage })}
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col gap-3 pt-2 sm:flex-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/groups')}
              className="min-h-[48px] w-full px-6 text-[15px] sm:w-auto"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={!canCreateGroup || isLoading}
              className={`min-h-[48px] w-full text-[15px] sm:flex-1 ${
                !canCreateGroup ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={!canCreateGroup ? (e) => {
                e.preventDefault();
                setShowUpgradeModal(true);
              } : undefined}
            >
              {!canCreateGroup ? 'Limiet bereikt - Upgrade nodig' : 'Groep aanmaken'}
            </Button>
          </div>
        </form>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          upgradeType="EXTRA_GROUP_SLOTS"
          onUpgradeComplete={() => {
            setShowUpgradeModal(false);
            checkGroupLimit(); // Refresh limits after upgrade
          }}
        />
      )}

      {/* Notice Container */}
      <NoticeContainer />
    </div>
  );
};

export default CreateGroupPage;